const DB_NAME = 'codeframe-assets';
const STORE = 'blobs';
const VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

/** What we persist per asset. Bytes (not a Blob) so the data path never depends
 *  on Blob.arrayBuffer() — jsdom's Blob is unreadable, and IndexedDB structured-
 *  clones ArrayBuffer faithfully everywhere. */
interface AssetRecord {
  bytes: ArrayBuffer;
  type: string;
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const store = db.transaction(STORE, mode).objectStore(STORE);
        const req = fn(store);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

/** Read a blob's bytes. jsdom's Blob lacks arrayBuffer(); fall back to FileReader. */
export async function blobArrayBuffer(b: Blob): Promise<ArrayBuffer> {
  if (typeof b.arrayBuffer === 'function') return b.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as ArrayBuffer);
    fr.onerror = () => reject(fr.error);
    fr.readAsArrayBuffer(b);
  });
}

/** SHA-256 of the bytes → "sha256-<first 32 hex chars>". */
async function hashBytes(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const hex = Array.from(new Uint8Array(digest))
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('');
  return `sha256-${hex.slice(0, 32)}`;
}

export const assetStore = {
  /** Store a blob under its content hash (dedupes). Returns the asset id. */
  async put(b: Blob): Promise<string> {
    return this.putBytes(await blobArrayBuffer(b), b.type || '');
  },
  /** Store raw bytes under their content hash. Used by bundle import. */
  async putBytes(bytes: ArrayBuffer, type = ''): Promise<string> {
    const id = await hashBytes(bytes);
    if (await this.has(id)) return id; // dedupe — skip the write
    const record: AssetRecord = { bytes, type };
    await tx('readwrite', (s) => s.put(record, id));
    return id;
  },
  /** Raw bytes for an asset (no Blob read needed). Used by bundle export. */
  async getBytes(id: string): Promise<ArrayBuffer | null> {
    const rec = await tx<AssetRecord | undefined>('readonly', (s) => s.get(id));
    return rec?.bytes ?? null;
  },
  /** A Blob reconstructed from the stored bytes (for createObjectURL). */
  async get(id: string): Promise<Blob | null> {
    const rec = await tx<AssetRecord | undefined>('readonly', (s) => s.get(id));
    return rec ? new Blob([rec.bytes], { type: rec.type }) : null;
  },
  async has(id: string): Promise<boolean> {
    const v = await tx<IDBValidKey | undefined>('readonly', (s) => s.getKey(id));
    return v !== undefined;
  },
  async delete(id: string): Promise<void> {
    await tx('readwrite', (s) => s.delete(id));
  },
  async keys(): Promise<string[]> {
    const ks = await tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
    return ks.map(String);
  },
};
